import type { SupabaseClient } from '@supabase/supabase-js';
import {
  adminGetUser,
  adminSearchUsers,
  adminSetRole,
  type AdminUserDetail,
} from './accountActions';
import {
  assignableRoles,
  canEditTarget,
  canUseAdminTools,
  parseRole,
  roleDisplayName,
  type Role,
} from './roles';
import { el, labeled } from './dom';
import { buildCommandConsole } from './commandUi';
import { openCodeMenu } from './codesAdminUi';

// The Admin panel. Everything here is cosmetic gating — the SECURITY DEFINER
// RPCs re-check the caller's role and rule 5 server-side on every call.
export function buildAdminPanel(
  client: SupabaseClient,
  self: { role: Role; userId: string | null; onBack: () => void },
): HTMLDivElement {
  const back = el('button', { className: 'account-secondary', testid: 'admin-back' }, 'Back');
  back.addEventListener('click', self.onBack);

  // Dev tiers: the command console is their whole panel.
  if (!canUseAdminTools(self.role)) {
    return el(
      'div',
      { className: 'account-panel', testid: 'account-panel-admin' },
      el('h2', {}, 'Admin panel'),
      buildCommandConsole(client, self.role),
      back,
    );
  }

  const error = el('div', { className: 'account-error', testid: 'admin-error' });
  const notice = el('div', { className: 'account-notice', testid: 'admin-notice' });
  const search = el('input', { type: 'text', maxLength: 20, testid: 'admin-search-input' });
  const results = el('div', { className: 'admin-results', testid: 'admin-search-results' });
  const detail = el('div', { className: 'admin-detail', testid: 'admin-user-detail' });

  // Same debounce shape as the username availability watcher.
  let timer: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const query = search.value;
      error.textContent = '';
      void adminSearchUsers(client, query).then((r) => {
        if (search.value !== query) return; // stale response
        if (!r.ok) {
          error.textContent = r.error;
          return;
        }
        results.replaceChildren(
          ...r.users.map((u) => {
            const row = el('button', { testid: `admin-result-${u.username}` }, u.username);
            row.addEventListener('click', () => {
              notice.textContent = '';
              void showDetail(u.id);
            });
            return row;
          }),
        );
      });
    }, 400);
  });

  async function showDetail(id: string): Promise<void> {
    error.textContent = '';
    const r = await adminGetUser(client, id);
    if (!r.ok) {
      error.textContent = r.error;
      return;
    }
    detail.replaceChildren(renderDetail(r.user));
  }

  function renderDetail(user: AdminUserDetail): HTMLElement {
    const fields = el(
      'dl',
      {},
      el('dt', {}, 'Player'),
      el('dd', { testid: 'admin-detail-username' }, user.username),
      el('dt', {}, 'Role'),
      el('dd', { testid: 'admin-detail-role' }, roleDisplayName(user.role)),
      el('dt', {}, 'Joined'),
      el('dd', {}, new Date(user.createdAt).toLocaleDateString()),
      el('dt', {}, 'Coins'),
      el('dd', { testid: 'admin-detail-coins' }, String(user.coins ?? 0)),
      el('dt', {}, 'Unlocked level'),
      el('dd', {}, String(user.unlocked ?? 1)),
      el('dt', {}, 'Bags'),
      el('dd', {}, String(user.bags ?? 0)),
      el('dt', {}, 'Last save'),
      el('dd', {}, user.saveUpdatedAt ? new Date(user.saveUpdatedAt).toLocaleString() : '—'),
    );
    if (!canEditTarget(user.role, user.id, self.userId)) {
      return el(
        'div',
        {},
        fields,
        el(
          'div',
          { className: 'account-notice', testid: 'admin-role-locked' },
          "This account's role can't be changed here.",
        ),
      );
    }
    const select = el('select', { testid: 'admin-role-select' });
    for (const role of assignableRoles) {
      const option = document.createElement('option');
      option.value = role;
      option.textContent = roleDisplayName(role);
      option.selected = role === user.role;
      select.append(option);
    }
    const save = el('button', { testid: 'admin-role-save' }, 'Set role');
    save.addEventListener('click', () => {
      error.textContent = '';
      void adminSetRole(client, user.id, parseRole(select.value)).then(async (r) => {
        if (!r.ok) {
          error.textContent = r.error;
          return;
        }
        // Refresh first: showDetail is also the reset for a new selection, so
        // the success notice must land after it, not be wiped by it.
        await showDetail(user.id);
        notice.textContent = 'Role updated.';
      });
    });
    return el('div', {}, fields, select, save);
  }

  // Redeem codes (spec 2026-10-09): admins only — this branch is never built
  // for a dev tier, and admin_* refuse anyone else server-side.
  const codeMenu = el('button', { testid: 'admin-code-menu' }, 'Go to code menu');
  codeMenu.addEventListener('click', () => openCodeMenu(client));

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-admin' },
    el('h2', {}, 'Admin panel'),
    buildCommandConsole(client, self.role),
    labeled('Find player', search),
    codeMenu,
    results,
    detail,
    error,
    notice,
    back,
  );
}
